/**
 * SYM-6351. The campaign's job title and location on its v2 ads: one value per campaign, each
 * Automatic (follows Targeting), Custom (the customer's own words) or Hidden (nothing drawn).
 *
 * Everything the editor decides about these two fields lives here rather than in the component,
 * because the app has no component tests and these are the rules the test book checks by hand
 * (scenes 1-5 and 9). The server holds the same rules (CampaignValues.php); this file only decides
 * what the editor SHOWS and what it SENDS.
 */

export type ValueMode = 'automatic' | 'custom' | 'hidden';
export type ValueField = 'title' | 'location';
export type OutdatedField = 'title' | 'location';

export interface FieldValue {
  mode: ValueMode;
  text: string | null;
}

/** What `include=campaignValues` answers (CampaignValuesService::editorState). */
export interface CampaignValuesState {
  ad_values: {
    title: FieldValue & { automatic: string };
    location: FieldValue & { automatic_names: string[] };
  };
  copied_from_values: Partial<Record<ValueField, FieldValue>>;
  ad_texts_language: { id: number; code: string; name: string } | null;
  restart_updated_ads_count: number | null;
  outdated_ads_count: number;
  outdated_fields: OutdatedField[];
  live_ads: {
    outdated_count: number;
    outdated_fields: OutdatedField[];
    show_message: boolean;
  };
  old_values: { title: string | null; location: string | null };
  current_values: { title: string; location: string };
  /** Whether the designs' fixed words exist in the campaign's language (else English). */
  fixed_phrases?: {
    available: boolean;
    language: string | null;
    shown_in: string;
    /** 'English' when "Translate these texts" goes to English instead (no fixed words). */
    translate_to?: string | null;
  };
  ads: {
    id: number;
    has_run: boolean;
    is_live: boolean;
    outdated_since: string | null;
    outdated_fields: OutdatedField[];
  }[];
}

/** X-1: the server refuses longer (SaveAdValuesRequest), so the input stops here. */
export const MAX_LENGTH: Record<ValueField, number> = {
  title: 80,
  location: 40,
};

/** D20, Mark's labels. */
export const MODE_LABEL: Record<ValueMode, string> = {
  automatic: 'Automatic (from Targeting)',
  custom: 'Custom',
  hidden: 'Hidden',
};

export const FIELD_LABEL: Record<ValueField, string> = {
  title: 'Job title',
  location: 'Location',
};

/**
 * The fixed short label for a list of places: two names, then "+N" (M-3). The pictures fit as many
 * whole names as each design has room for; a link has no width, so it uses the fixed form, the same
 * one the server writes (LocationFormatter::format).
 */
export const formatPlaces = (names: string[]): string => {
  const clean = names.map((n) => n.trim()).filter((n) => n !== '');
  if (!clean.length) return '';
  const shown = clean.slice(0, 2).join(' / ');
  return clean.length > 2 ? `${shown} +${clean.length - 2}` : shown;
};

/** What Automatic would show for this field right now. */
export const automaticText = (
  field: ValueField,
  state: CampaignValuesState
): string =>
  field === 'title'
    ? state.ad_values.title.automatic
    : formatPlaces(state.ad_values.location.automatic_names);

/** The campaign's saved value for a field. */
export const savedValue = (
  field: ValueField,
  state: CampaignValuesState
): FieldValue => ({
  mode: state.ad_values[field].mode,
  text: state.ad_values[field].text ?? null,
});

/** What the input box shows for a value: Automatic shows what it follows, Hidden shows nothing. */
export const shownText = (
  field: ValueField,
  value: FieldValue,
  state: CampaignValuesState
): string => {
  if (value.mode === 'automatic') return automaticText(field, state);
  if (value.mode === 'custom') return value.text ?? '';
  return '';
};

/**
 * What typing into the field means (D2, D13, M-2):
 * - empty, or only spaces: Hidden;
 * - exactly what Automatic shows: back to Automatic, so it keeps following Targeting;
 * - anything else: Custom.
 */
export const valueFromTyped = (
  field: ValueField,
  typed: string,
  state: CampaignValuesState
): FieldValue => {
  const trimmed = typed.trim();
  if (trimmed === '') return { mode: 'hidden', text: null };
  if (trimmed === automaticText(field, state).trim())
    return { mode: 'automatic', text: null };
  return { mode: 'custom', text: typed };
};

/** Scene 4. The Reset link, shown whenever the field is not Automatic. */
export const resetLinkText = (
  field: ValueField,
  state: CampaignValuesState
): string =>
  field === 'title'
    ? 'Reset the campaign title'
    : `Reset the targeting location (${automaticText('location', state)})`;

/** The line under the field. */
export const fieldHint = (field: ValueField, mode: ValueMode): string => {
  if (mode === 'automatic')
    return 'Used on all ads in this campaign that are not published yet.';
  if (mode === 'custom') return 'Targeting changes no longer update this.';
  return field === 'title'
    ? 'No job title is shown on the ads.'
    : 'No location is shown on the ads.';
};

/** Its own unsaved state (S3-d): the field is dirty when what would be sent differs from what is saved. */
export const isValueDirty = (saved: FieldValue, draft: FieldValue): boolean =>
  saved.mode !== draft.mode ||
  (draft.mode === 'custom' && (saved.text ?? '') !== (draft.text ?? ''));

/**
 * Scene 9 (D22, D30, X-3). What a copy offers from the campaign it was copied from, while its own
 * field is still Automatic. The server only sends an offer when that holds.
 */
export const copyOfferText = (
  field: ValueField,
  state: CampaignValuesState
): { text: string; button: string; value: FieldValue } | null => {
  const offer = state.copied_from_values?.[field];
  if (!offer || state.ad_values[field].mode !== 'automatic') return null;

  const noun = field === 'title' ? 'job title' : 'location';
  if (offer.mode === 'hidden') {
    return {
      text: `The original hid the ${noun}. Hide it here too?`,
      button: 'Hide it here too',
      value: { mode: 'hidden', text: null },
    };
  }
  if (offer.mode === 'custom' && offer.text) {
    return {
      text: `The original campaign used the custom ${noun} "${offer.text}".`,
      button: 'Use it here too',
      value: { mode: 'custom', text: offer.text },
    };
  }
  return null;
};

/** What the save endpoint takes for one field. */
export const toPayload = (value: FieldValue): FieldValue => ({
  mode: value.mode,
  text: value.mode === 'custom' ? value.text : null,
});
